import React, { useState, useRef, useEffect } from 'react';
import { Switch, Button, Col, Drawer, Form, Input, Row, Select, Space, message, Typography, InputNumber } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { getList as getAllConcepts } from '../../../../apis/ConceptoApi';
import { insert, update, getById } from '../../../../apis/TipoLiquidacionApi';

const { Title } = Typography;
const { Option } = Select;

const App = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [concepts, setConcepts] = useState([]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            return;
        }

        const fields = form.getFieldsValue();
        const payload = {
            tipo: fields.tipo,
            descripcion: fields.descripcion,
            prima_antiguedad_dias: fields.prima_antiguedad_dias || 0,
            prima_antiguedad_meses: fields.prima_antiguedad_meses || 0,
            prima_antiguedad_monto: fields.prima_antiguedad_monto || 0,
            compensacion_fija_dias: fields.compensacion_fija_dias || 0,
            compensacion_fija_meses: fields.compensacion_fija_meses || 0,
            compensacion_fija_monto: fields.compensacion_fija_monto || 0,
            compensacion_variable_dias: fields.compensacion_variable_dias || 0,
            compensacion_variable_meses: fields.compensacion_variable_meses || 0,
            compensacion_variable_monto: fields.compensacion_variable_monto || 0,
            condicion_prima_antiguedad_anos: fields.condicion_prima_antiguedad_anos || 0,
            condicion_compensacion_fija_anos: fields.condicion_compensacion_fija_anos || 0,
            condicion_compensacion_variable_anos: fields.condicion_compensacion_variable_anos || 0,
            activo: fields.activo
        };

        let success = false;
        
        if (ID > 0) {
            const response = await update({ id: ID, ...payload });
            success = response.ok;
            if (response.ok) {
                message.success('Se actualizó correctamente el tipo de liquidación!');
            }
        } else {
            const response = await insert(payload);
            success = response.ok;
            if (response.ok) {
                setID(response.payload);
                message.success('Se guardó correctamente el tipo de liquidación!');
            }
        }

        if (success) {
            setModified(true);
            onClose(true);
        }
    };

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
    };

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                await getConcepts();
                if (ID > 0) {
                    await refreshData();
                } else {
                    setInitialValues({
                        activo: true,
                        prima_antiguedad_dias: 0,
                        prima_antiguedad_meses: 0,
                        prima_antiguedad_monto: 0,
                        compensacion_fija_dias: 0,
                        compensacion_fija_meses: 0,
                        compensacion_fija_monto: 0,
                        compensacion_variable_dias: 0,
                        compensacion_variable_meses: 0,
                        compensacion_variable_monto: 0,
                        condicion_prima_antiguedad_anos: 0,
                        condicion_compensacion_fija_anos: 0,
                        condicion_compensacion_variable_anos: 0
                    });
                }
                setModified(false);
            })();
        }
        return () => effectRan.current = true;
    }, []);

    const refreshData = async () => {
        const response = await getById(ID);
        if (response.ok) {
            let tipoLiquidacion = response.payload;
            setInitialValues({
                tipo: tipoLiquidacion.tipo,
                descripcion: tipoLiquidacion.descripcion,
                prima_antiguedad_dias: tipoLiquidacion.prima_antiguedad_dias,
                prima_antiguedad_meses: tipoLiquidacion.prima_antiguedad_meses,
                prima_antiguedad_monto: tipoLiquidacion.prima_antiguedad_monto,
                compensacion_fija_dias: tipoLiquidacion.compensacion_fija_dias,
                compensacion_fija_meses: tipoLiquidacion.compensacion_fija_meses,
                compensacion_fija_monto: tipoLiquidacion.compensacion_fija_monto,
                compensacion_variable_dias: tipoLiquidacion.compensacion_variable_dias,
                compensacion_variable_meses: tipoLiquidacion.compensacion_variable_meses,
                compensacion_variable_monto: tipoLiquidacion.compensacion_variable_monto,
                condicion_prima_antiguedad_anos: tipoLiquidacion.condicion_prima_antiguedad_anos,
                condicion_compensacion_fija_anos: tipoLiquidacion.condicion_compensacion_fija_anos,
                condicion_compensacion_variable_anos: tipoLiquidacion.condicion_compensacion_variable_anos,
                activo: tipoLiquidacion.activo
            });
        }
    };

    const getConcepts = async () => {
        const response = await getAllConcepts();
        if (response.ok) {
            setConcepts(response.payload);
        }
    };

    return (
        <Drawer
            title={title}
            width={900}
            placement="right"
            onClose={onClose}
            open={open}
            closable={true}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button onClick={handleOk} type="primary">Guardar</Button>
                </Space>
            }
        >
            {initialValues && (
                <Form
                    layout="vertical"
                    form={form}
                    autoComplete="off"
                    initialValues={initialValues}
                    onFinish={handleOk}
                >
                    {/* Información General */}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="tipo"
                                label="Tipo de Liquidación"
                                rules={[{ required: true, message: 'Por favor ingresa el tipo de liquidación' }]}
                            >
                                <Input placeholder="Ej: LIQ1MES" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="descripcion"
                                label="Descripción"
                                rules={[{ required: true, message: 'Por favor ingresa la descripción' }]}
                            >
                                <Input placeholder="Ej: LIQUIDACION X 1 MES" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Prima de Antigüedad */}
                    <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                        Prima de Antigüedad
                    </Title>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="prima_antiguedad_dias"
                                label="Días"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                    addonAfter="x año"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="prima_antiguedad_meses"
                                label="Meses"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                    addonAfter="x año"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="prima_antiguedad_monto"
                                label="Monto"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="$0.00"
                                    style={{ width: '100%' }}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Compensación Fija */}
                    <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                        Compensación Fija
                    </Title>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="compensacion_fija_dias"
                                label="Días"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="30.00"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="compensacion_fija_meses"
                                label="Meses"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="compensacion_fija_monto"
                                label="Monto"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="$0.00"
                                    style={{ width: '100%' }}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Compensación Variable */}
                    <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                        Compensación Variable
                    </Title>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="compensacion_variable_dias"
                                label="Días"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                    addonAfter="x año"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="compensacion_variable_meses"
                                label="Meses"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                    addonAfter="x año"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="compensacion_variable_monto"
                                label="Monto"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="$0.00"
                                    style={{ width: '100%' }}
                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Condiciones - Antigüedad mínima requerida */}
                    <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                        Condiciones, Antigüedad mínima requerida en años
                    </Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="condicion_prima_antiguedad_anos"
                                label="Prima de Antigüedad"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="condicion_compensacion_fija_anos"
                                label="Compensación Fija"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="condicion_compensacion_variable_anos"
                                label="Compensación Variable"
                            >
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Estado Activo */}
                    <Row gutter={16} style={{ marginTop: 24 }}>
                        <Col span={12}>
                            <Form.Item
                                name="activo"
                                label="Activo"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren={<CheckOutlined />}
                                    unCheckedChildren={<CloseOutlined />}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}
        </Drawer>
    );
};

export default App;